import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { shareReplay, switchMap } from 'rxjs/operators';
import { ILoginCheckPostBody, ILoginCheckPostResponse, IUser } from './auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private router: Router,
    private http: HttpClient,
    @Inject('environment') private environment,
  ) {
    const tokenRegExp = /[&]*aitarget_jwt_token=([^&#^=]*)/;
    const tokenFromUrl = tokenRegExp.exec(location.href);

    if (tokenFromUrl) {
      localStorage.setItem('jwt', tokenFromUrl[1]);
      // Replace page URL with token removed and without saving page with token to History
      // @see https://developer.mozilla.org/en-US/docs/Web/API/History_API#The_replaceState()_method
      history.replaceState(null, (window as any).title, location.href.replace(tokenRegExp, '')
                                                       .replace(/\?[&]*/, '?')
                                                       .replace(/\?$/, ''));
    }
  }

  public user$: Observable<IUser> = this.http.get<IUser>(`${this.environment.host}/facebook/user/me`).pipe(
    shareReplay(1)
  );
  private authError$$ = new BehaviorSubject(null);
  private authError$ = this.authError$$.asObservable();

  public setAuthError(err: any) {
    this.authError$$.next(err);
  }

  public getAuthError$() {
    return this.authError$;
  }

  public isAuthorized$(): Observable<boolean> {
    if (!this.getToken()) {
      return throwError({
        type: 'jwt'
      });
    }

    // try to get user
    return this.user$.pipe(
      switchMap((user: IUser) => {
        // check if user is moderated
        if (!user.moderated) {
          return throwError({
            user,
            type: 'not_moderated'
          });
        }

        return of(Boolean(user));
      })
    );
  }

  /**
   * Get user's data
   */
  public getUser$(): Observable<IUser> {
    return this.user$;
  }

  /**
   * Logout user
   */
  public logout() {
    this.http.post(`${this.environment.host}/logout`, {}).subscribe((response: any) => {
      localStorage.removeItem('jwt');
      location.reload();
    });
  }

  /**
   * Get user JWT token
   * $returns {string}
   */
  public getToken() {
    return localStorage.getItem('jwt');
  }

  public loginCheck(params: ILoginCheckPostBody): Observable<ILoginCheckPostResponse> {
    return this.http.post<ILoginCheckPostResponse>(`${this.environment.host}/login_check`, params);
  }
}
